import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  Avatar,
  Fade,
  Grow,
  Button,
  Container,
  Slide,
} from '@mui/material';
import {
  People as PeopleIcon,
  Event as EventIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  LocalHospital as LocalHospitalIcon,
  MonitorHeart,
  EmergencyShare as Emergency,
  ArrowUpward,
  ArrowDownward,
  CalendarToday,
  Receipt as BillingIcon,
  LocalPharmacy as PharmacyIcon,
} from '@mui/icons-material';
import { Appointment } from '../types/Appointment';
import { appointmentService } from '../services/appointmentService';
import { patientService } from '../services/patientService';
import { formatDate, formatTime } from '../utils/formatters';

interface DashboardStats {
  totalPatients: number;
  totalAppointments: number;
  todaysAppointments: number;
  upcomingAppointments: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalPatients: 0,
    totalAppointments: 0,
    todaysAppointments: 0,
    upcomingAppointments: 0,
  });
  const [todaysAppointments, setTodaysAppointments] = useState<Appointment[]>([]);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [patients, appointments, todaysAppts, upcomingAppts] = await Promise.all([
          patientService.getAllPatients(),
          appointmentService.getAllAppointments(),
          appointmentService.getTodaysAppointments(),
          appointmentService.getUpcomingAppointments(7),
        ]);

        setStats({
          totalPatients: patients.length,
          totalAppointments: appointments.length,
          todaysAppointments: todaysAppts.length,
          upcomingAppointments: upcomingAppts.length,
        });

        setTodaysAppointments(todaysAppts);
        setUpcomingAppointments(upcomingAppts);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const formatAppointmentTime = (timeString: string | null | undefined) => {
    if (!timeString) return 'N/A';
    // Handle TimeSpan format (HH:mm:ss) from backend
    const date = new Date(`1970-01-01T${timeString}`);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatAppointmentDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'scheduled':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'no-show':
        return 'warning';
      default:
        return 'default';
    }
  };

  const StatCard = ({ 
    title, 
    value, 
    icon, 
    color, 
    gradient,
    change,
    changeType 
  }: { 
    title: string; 
    value: number; 
    icon: React.ReactNode; 
    color: string;
    gradient: string;
    change?: string;
    changeType?: 'increase' | 'decrease' | 'neutral';
  }) => (
    <Grow in={!loading} timeout={500}>
      <Card 
        className="card-hover"
        sx={{ 
          height: '100%',
          background: gradient,
          color: 'white',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 5,
          border: 'none',
          boxShadow: `0 20px 40px -12px ${color}40`,
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            right: 0,
            width: '60%',
            height: '100%',
            background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
            transform: 'translateX(100%)',
            transition: 'transform 0.6s ease',
          },
          '&:hover': {
            transform: 'translateY(-12px) scale(1.05)',
            boxShadow: `0 30px 60px -12px ${color}60`,
            '&::before': {
              transform: 'translateX(-100%)',
            },
          },
        }}
      >
        <CardContent sx={{ position: 'relative', zIndex: 1, p: 4 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255,255,255,0.9)', 
                  mb: 1,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.1em',
                  fontSize: '0.8rem',
                }}
              >
                {title}
              </Typography>
              <Typography 
                variant="h3" 
                component="div" 
                sx={{ 
                  fontWeight: 900,
                  mb: 1,
                  fontSize: '2.8rem',
                  lineHeight: 1,
                  fontFamily: 'Poppins, sans-serif',
                  textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  color: 'white',
                }}
              >
                {value.toLocaleString()}
              </Typography>
            </Box>
            <Avatar
              sx={{
                background: 'rgba(255, 255, 255, 0.25)',
                backdropFilter: 'blur(15px)',
                width: 70,
                height: 70,
                border: '2px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Box sx={{ fontSize: 35, color: 'white' }}>
                {icon}
              </Box>
            </Avatar>
          </Box>
          {change && (
            <Box display="flex" alignItems="center" gap={0.5}>
              {changeType === 'increase' ? (
                <ArrowUpward sx={{ fontSize: 20, color: '#10b981' }} />
              ) : changeType === 'decrease' ? (
                <ArrowDownward sx={{ fontSize: 20, color: '#ef4444' }} />
              ) : (
                <TrendingUpIcon sx={{ fontSize: 20, color: 'rgba(255,255,255,0.8)' }} />
              )}
              <Typography 
                variant="body2" 
                sx={{ 
                  color: changeType === 'increase' ? '#10b981' : 
                         changeType === 'decrease' ? '#ef4444' : 'rgba(255,255,255,0.9)',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                }}
              >
                {change}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grow>
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Box sx={{ textAlign: 'center' }}>
          <div className="loading-dots">
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
          <Typography variant="h6" sx={{ mt: 2, color: '#66b2b2' }}>
            Loading Dashboard...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ 
      py: 1,
      background: 'transparent',
      minHeight: '100vh'
    }}>
      {/* Spectacular Hero Section */}
      <Fade in={mounted} timeout={1000}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #2C7A9C 0%, #4A9BBD 50%, #6AB5D1 100%)',
            borderRadius: 6,
            p: 6,
            mb: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(44, 122, 156, 0.3)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: -50,
              right: -50,
              width: 300,
              height: 300,
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'float 8s ease-in-out infinite',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 200,
              height: 200,
              background: 'radial-gradient(circle, rgba(255, 255, 255, 0.05) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'float 6s ease-in-out infinite reverse',
            },
          }}
        >
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12} md={8}>
              <Typography 
                variant="h2" 
                component="h1" 
                sx={{ 
                  fontWeight: 900,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  mb: 2,
                  fontFamily: 'Poppins, sans-serif',
                  textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                  animation: 'slideInFromBottom 1s cubic-bezier(0.4, 0, 0.2, 1)',
                  color: 'white',
                }}
              >
                Welcome to SCULPTE by Plasticare
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 4,
                  opacity: 0.95,
                  fontSize: '1.3rem',
                  fontWeight: 400,
                  textShadow: '0 2px 10px rgba(0, 0, 0, 0.2)',
                  animation: 'slideInFromBottom 1s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both',
                }}
              >
                Your comprehensive hospital management dashboard - Monitor and manage all operations in real-time
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                <Grow in={mounted} timeout={1500}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={<ScheduleIcon />}
                    sx={{
                      background: 'rgba(255, 255, 255, 0.25)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      color: 'white',
                      fontWeight: 700,
                      px: 4,
                      py: 2.5,
                      fontSize: '1.1rem',
                      borderRadius: 4,
                      textTransform: 'none',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        background: 'rgba(255, 255, 255, 0.35)',
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
                      },
                    }}
                  >
                    View Today's Schedule
                  </Button>
                </Grow>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                animation: 'float 4s ease-in-out infinite',
                position: 'relative',
                zIndex: 1,
              }}>
                <Avatar
                  sx={{
                    width: { xs: 140, md: 180 },
                    height: { xs: 140, md: 180 },
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(15px)',
                    border: '4px solid rgba(255, 255, 255, 0.3)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  }}
                >
                  <MonitorHeart sx={{ fontSize: { xs: 70, md: 90 }, color: 'white' }} />
                </Avatar>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Fade>

      {/* Enhanced Stats Section */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Slide in={mounted} direction="up" timeout={800}>
            <Box>
              <StatCard
                title="Total Patients"
                value={stats.totalPatients}
                icon={<PeopleIcon />}
                color="#2C7A9C"
                gradient="linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)"
              />
            </Box>
          </Slide>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Slide in={mounted} direction="up" timeout={1000}>
            <Box>
              <StatCard
                title="Today's Appointments"
                value={stats.todaysAppointments}
                icon={<CalendarToday />}
                color="#4A9BBD"
                gradient="linear-gradient(135deg, #4A9BBD 0%, #2C7A9C 100%)"
              />
            </Box>
          </Slide>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Slide in={mounted} direction="up" timeout={1200}>
            <Box>
              <StatCard
                title="Total Appointments"
                value={stats.totalAppointments}
                icon={<EventIcon />}
                color="#367D9A"
                gradient="linear-gradient(135deg, #6AB5D1 0%, #4A9BBD 100%)"
              />
            </Box>
          </Slide>
        </Grid>
      </Grid>

      {/* New Management Cards */}
      <Grid container spacing={4} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6}>
          <Slide in={mounted} direction="up" timeout={1600}>
            <Card
              sx={{
                borderRadius: 5,
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 25px 50px -12px rgba(185, 28, 28, 0.3)',
                },
              }}
              onClick={() => window.location.href = '/billing'}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                      Billing Management
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                      Manage patient bills and payments
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 60,
                      height: 60,
                    }}
                  >
                    <BillingIcon sx={{ fontSize: 30, color: 'white' }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Slide in={mounted} direction="up" timeout={1800}>
            <Card
              sx={{
                borderRadius: 5,
                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                color: 'white',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 25px 50px -12px rgba(5, 150, 105, 0.3)',
                },
              }}
              onClick={() => window.location.href = '/pharmacy'}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ color: 'white' }}>
                      Pharmacy Management
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, color: 'white' }}>
                      Manage medicines and inventory
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.2)',
                      width: 60,
                      height: 60,
                    }}
                  >
                    <PharmacyIcon sx={{ fontSize: 30, color: 'white' }} />
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Slide>
        </Grid>
      </Grid>

      <Grid container spacing={4}>
        {/* Enhanced Today's Appointments */}
        <Grid item xs={12} md={6}>
          <Fade in={mounted} timeout={1500}>
            <Card
              sx={{
                borderRadius: 5,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.1)',
                boxShadow: '0 25px 50px -12px rgba(14, 165, 233, 0.15)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
                  <Avatar
                    sx={{
                      background: 'linear-gradient(135deg, #2C7A9C 0%, #1E5A73 100%)',
                      width: 60,
                      height: 60,
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 30 }} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 800,
                        color: '#2C7A9C',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      Today's Appointments
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '1rem' }}>
                      {todaysAppointments.length} appointments scheduled for today
                    </Typography>
                  </Box>
                </Box>
                
                {todaysAppointments.length === 0 ? (
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 6,
                      color: '#64748b'
                    }}
                  >
                    <LocalHospitalIcon sx={{ fontSize: 60, opacity: 0.5, mb: 2, color: '#66b2b2' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      No appointments scheduled for today
                    </Typography>
                    <Typography variant="body2">
                      Enjoy your peaceful day!
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {todaysAppointments.slice(0, 5).map((appointment, index) => (
                      <Grow in={!loading} timeout={300 + index * 100} key={appointment.appointmentId}>
                        <ListItem 
                          sx={{ 
                            borderRadius: 3,
                            mb: 2,
                            background: 'rgba(13, 148, 136, 0.03)',
                            border: '1px solid rgba(13, 148, 136, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(13, 148, 136, 0.08)',
                              transform: 'translateX(8px)',
                              boxShadow: '0 8px 25px rgba(13, 148, 136, 0.15)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontFamily: 'Poppins, sans-serif',
                                  }}
                                >
                                  {appointment.patientName}
                                </Typography>
                                <Chip
                                  label={appointment.status}
                                  color={getStatusColor(appointment.status)}
                                  size="small"
                                  sx={{ 
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                  }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body1" sx={{ color: '#2C7A9C', fontWeight: 600, mb: 0.5 }}>
                                  {formatAppointmentTime(appointment.appointmentTime)} - Dr. {appointment.doctorName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {appointment.appointmentType || 'Consultation'} {appointment.reason ? `• ${appointment.reason}` : ''}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Grow>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>

        {/* Enhanced Upcoming Appointments */}
        <Grid item xs={12} md={6}>
          <Fade in={mounted} timeout={1700}>
            <Card
              sx={{
                borderRadius: 5,
                background: 'rgba(255, 255, 255, 0.9)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(14, 165, 233, 0.1)',
                boxShadow: '0 25px 50px -12px rgba(14, 165, 233, 0.15)',
                overflow: 'hidden',
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box display="flex" alignItems="center" gap={2} sx={{ mb: 3 }}>
                  <Avatar
                    sx={{
                      background: 'linear-gradient(135deg, #4A9BBD 0%, #2C7A9C 100%)',
                      width: 60,
                      height: 60,
                    }}
                  >
                    <EventIcon sx={{ fontSize: 30 }} />
                  </Avatar>
                  <Box>
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 800,
                        color: '#1E5A73',
                        fontFamily: 'Poppins, sans-serif',
                      }}
                    >
                      Upcoming Appointments
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '1rem' }}>
                      Next 7 days overview ({upcomingAppointments.length} total)
                    </Typography>
                  </Box>
                </Box>
                
                {upcomingAppointments.length === 0 ? (
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 6,
                      color: '#64748b'
                    }}
                  >
                    <EventIcon sx={{ fontSize: 60, opacity: 0.5, mb: 2, color: '#66b2b2' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      No upcoming appointments
                    </Typography>
                    <Typography variant="body2">
                      Your schedule is clear for the week
                    </Typography>
                  </Box>
                ) : (
                  <List sx={{ p: 0 }}>
                    {upcomingAppointments.slice(0, 5).map((appointment, index) => (
                      <Grow in={!loading} timeout={400 + index * 100} key={appointment.appointmentId}>
                        <ListItem 
                          sx={{ 
                            borderRadius: 3,
                            mb: 2,
                            background: 'rgba(20, 184, 166, 0.03)',
                            border: '1px solid rgba(20, 184, 166, 0.1)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(6, 182, 212, 0.08)',
                              transform: 'translateX(8px)',
                              boxShadow: '0 8px 25px rgba(6, 182, 212, 0.15)',
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography 
                                  variant="h6" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontFamily: 'Poppins, sans-serif',
                                  }}
                                >
                                  {appointment.patientName}
                                </Typography>
                                <Chip
                                  label={appointment.status}
                                  color={getStatusColor(appointment.status)}
                                  size="small"
                                  sx={{ 
                                    fontWeight: 600,
                                    textTransform: 'capitalize',
                                  }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ mt: 1.5 }}>
                                <Typography variant="body1" sx={{ color: '#06b6d4', fontWeight: 600, mb: 0.5 }}>
                                  {formatAppointmentDate(appointment.appointmentDate)} at {formatAppointmentTime(appointment.appointmentTime)}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Dr. {appointment.doctorName} • {appointment.appointmentType || 'Consultation'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      </Grow>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Fade>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;
